import { YourCarNavigationActions } from '@app/features/your-car/state/actions';
import { YourDriversNavigationActions } from '@app/features/your-drivers/state/actions';
import { PremiumNavigationActions } from '@app/features/premium/state/actions';
import { ReviewNavigationActions } from '@app/features/review/state/actions';
import { PaymentNavigationActions } from '@app/features/payment/state/actions';
import { DashboardNavigationActions } from '@app/features/dashboard/state/actions';
import { RefundNavigationActions } from '@app/features/refund/state/actions';
import { YourAddressNavigationActions } from '@app/features/your-addresses/state/actions';
import { YourRegistrationNavigationActions } from '@app/features/your-registration/state/actions';
import { AllocateNavigationActions } from '@app/features/allocate/state/actions';
import { YourDetailsNavigationActions } from '@app/features/your-details/state/actions';
import { YourMileageNavigationActions } from '@app/features/your-mileage/state/actions';
import { YourConvictionsNavigationActions } from '@app/features/your-convictions/state/actions';
import { YourVehicleUsageNavigationActions } from '@app/features/your-vehicle-usage/state/actions';
import { YourTemporaryEuropeanCoverNavigationActions } from '@app/features/your-temporary-european-cover/state/actions';
import { WhereIparkMyCarNavigationActions } from '@app/features/where-ipark-my-car/state/actions';

import { FeatureState } from '@global/infrastructure/interfaces/feature-state';
import { JourneyFeatures } from '@app/infrastructure/data/journey-features';

import {
  ADD_DRIVER_PAGE_TITLE,
  CONFIRM_PAGE_TITLE,
  DASHBOARD_PAGE_TITLE,
  RENEWAL_PREFERENCE_TITLE,
  ERROR_PAGE_TITLE,
  LOADING_PAGE_TITLE,
  PAYMENT_PAGE_TITLE,
  PAYMENT_DIRECT_DEBIT_EDIT_PAGE_TITLE,
  PREMIUM_PAGE_TITLE,
  REFUND_PAGE_TITLE,
  REVIEW_PAGE_TITLE,
  YOUR_CAR_PAGE_TITLE,
  YOUR_TEMPORARY_EUROPEAN_COVER_PAGE_TITLE,
  YOUR_DRIVER_PAGE_TITLE,
  ALLOCATE_PAGE_TITLE,
  BILLING_PAGE_TITLE,
  RETRIEVE_QUOTE_TITLE,
  YOUR_ADDRESS_PAGE_TITLE,
  YOUR_PERSONAL_DETAILS_PAGE_TITLE,
  CHOOSE_MAIN_DRIVER_PAGE_TITLE,
  YOUR_MILEAGE_PAGE_TITLE,
  YOUR_CAR_REGISTRATION_MILEAGE_PAGE_TITLE,
  YOUR_CONVICTIONS_PAGE_TITLE,
  YOUR_VEHICLE_USAGE_PAGE_TITLE,
  WHERE_IPARK_MY_CAR_TITLE,
  YOUR_ADDRESS_POSTAL_PAGE_TITLE,
  YOUR_ADDRESSES_PAGE_TITLE,
  DRIVER_INFORMATION_PAGE_TITLE,
  YOUR_LICENCE_NUMBER_PAGE_TITLE,
  YOUR_DRIVING_HISTORY_PAGE_TITLE,
  PREFERRED_PAYMENT_DATE_TITLE,
} from '@app/infrastructure/constants';
import { RenewalPreferenceNavigationActions } from '@app/features/renewal-preference/state/actions';
import { MidTermCancellationNavigationActions } from '@app/features/mid-term-cancellation/state/actions';

const loadPolicy: FeatureState = {
  panelConfig: {
    isVisible: false,
    pageTitle: LOADING_PAGE_TITLE,
  },
  navConfig: [],
};

const billing: FeatureState = {
  panelConfig: {
    isVisible: false,
    pageTitle: BILLING_PAGE_TITLE,
  },
  navConfig: [],
};

const retrieveQuote: FeatureState = {
  panelConfig: {
    isVisible: false,
    pageTitle: RETRIEVE_QUOTE_TITLE,
  },
  navConfig: [],
};

export const changeType: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: false,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Are you making a permanent or temporary change?',
    breadcrumbFeatureName: JourneyFeatures.changeType.name,
    pageTitle: DASHBOARD_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Cancel',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(DashboardNavigationActions.backButtonClicked()),
      theme: null,
      isDisabled: false,
    },
  ],
};

export const changeDate: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: false,
    isShowPolicyNumber: false,
    mainTitle: 'Make a change',
    subTitle: 'When do you want this change to start?',
    breadcrumbFeatureName: JourneyFeatures.changeDate.name,
    pageTitle: DASHBOARD_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(DashboardNavigationActions.backButtonClicked()),
      theme: null,
      isDisabled: false,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      isDisabled: false,
      onClick: (store) =>
        store.dispatch(DashboardNavigationActions.nextButtonClicked()),
    },
  ],
};

export const dashboardSelect: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: false,
    isShowPolicyNumber: false,
    mainTitle: 'Make a change',
    subTitle: 'What would you like to change?',
    priceAffectedPerm: `Some changes you make may affect the price of your insurance quote. As you're making a change to your policy, please check to make sure your <span class="section__highlight">cover type, excess and extras</span> still meet your needs.`,
    priceAffectedTemp: `Some changes you make may affect the price of your insurance quote.`,
    isHideSubTitleContainer: true,
    breadcrumbFeatureName: JourneyFeatures.dashboardSelect.name,
    pageTitle: DASHBOARD_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(DashboardNavigationActions.backButtonClicked()),
      theme: null,
      isDisabled: false,
    },
    {
      label: 'Start quote',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      isDisabled: false,
      onClick: (store) =>
        store.dispatch(DashboardNavigationActions.nextButtonClicked()),
    },
  ],
};

export const dashboardUpdate: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: false,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Please update your details',
    breadcrumbFeatureName: JourneyFeatures.dashboardUpdate.name,
    pageTitle: DASHBOARD_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Cancel',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(DashboardNavigationActions.backButtonClicked()),
      theme: null,
      isDisabled: false,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Next',
      theme: 'primary',
      isDisabled: false,
      onClick: (store) =>
        store.dispatch(DashboardNavigationActions.nextButtonClicked()),
    },
  ],
};

export const renewalPreference: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: '',
    subTitle: 'Renewal preference',
    breadcrumbFeatureName: JourneyFeatures.renewalPreference.name,
    pageTitle: RENEWAL_PREFERENCE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(RenewalPreferenceNavigationActions.backButtonClicked()),
      theme: null,
      isDisabled: false,
    },
    {
      label: 'Save',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      isDisabled: false,
      onClick: (store) =>
        store.dispatch(RenewalPreferenceNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourCarPerm: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Change your car',
    breadcrumbFeatureName: JourneyFeatures.yourCarPerm.name,
    pageTitle: YOUR_CAR_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourCarNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourCarNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourCarTemp: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Add a temporary car',
    breadcrumbFeatureName: JourneyFeatures.yourCarTemp.name,
    pageTitle: YOUR_CAR_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourCarNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourCarNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourTemporaryEuropeanCover: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Your Temporary European Cover',
    subTitle: 'Add a European cover',
    breadcrumbFeatureName: JourneyFeatures.yourTemporaryEuropeanCover.name,
    pageTitle: YOUR_TEMPORARY_EUROPEAN_COVER_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(
          YourTemporaryEuropeanCoverNavigationActions.backButtonClicked()
        ),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(
          YourTemporaryEuropeanCoverNavigationActions.nextButtonClicked()
        ),
    },
  ],
};

export const yourMileage: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Change your mileage',
    breadcrumbFeatureName: JourneyFeatures.yourMileage.name,
    pageTitle: YOUR_MILEAGE_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourMileageNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourMileageNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourDetails: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'My personal details',
    breadcrumbFeatureName: JourneyFeatures.yourDetails.name,
    pageTitle: YOUR_PERSONAL_DETAILS_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourDetailsNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourDetailsNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourLicenceNumber: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'My licence number',
    breadcrumbFeatureName: JourneyFeatures.yourLicenceNumber.name,
    pageTitle: YOUR_LICENCE_NUMBER_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourDetailsNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Skip',
      id: 'skip',
      css: 'section__button-list',
      ariaLabel: 'Skip',
      theme: null,
      onClick: (store) =>
        store.dispatch(YourDetailsNavigationActions.skipButtonClicked()),
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourDetailsNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourDrivingHistory: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'My driving history',
    breadcrumbFeatureName: JourneyFeatures.yourDrivingHistory.name,
    pageTitle: YOUR_DRIVING_HISTORY_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourDetailsNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourDetailsNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourRegistration: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Change your registration',
    breadcrumbFeatureName: JourneyFeatures.yourRegistration.name,
    pageTitle: YOUR_CAR_REGISTRATION_MILEAGE_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourRegistrationNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourRegistrationNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourAddresses: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Change your addresses',
    breadcrumbFeatureName: JourneyFeatures.yourAddresses.name,
    pageTitle: YOUR_ADDRESSES_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourAddressNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourAddressNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourAddress: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Change your policy address',
    breadcrumbFeatureName: JourneyFeatures.yourAddress.name,
    pageTitle: YOUR_ADDRESS_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourAddressNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourAddressNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourPostalAddress: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Change your postal address',
    breadcrumbFeatureName: JourneyFeatures.yourPostalAddress.name,
    pageTitle: YOUR_ADDRESS_POSTAL_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourAddressNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourAddressNavigationActions.nextButtonClicked()),
    },
  ],
};

export const allocate: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Allocate drivers and cars',
    breadcrumbFeatureName: JourneyFeatures.allocate.name,
    pageTitle: ALLOCATE_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(AllocateNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(AllocateNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourDriversPerm: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Add/remove or edit drivers',
    breadcrumbFeatureName: JourneyFeatures.yourDriversPerm.name,
    pageTitle: YOUR_DRIVER_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourDriversTemp: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Add or remove drivers',
    breadcrumbFeatureName: JourneyFeatures.yourDriversTemp.name,
    pageTitle: YOUR_DRIVER_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourDriversMoreInfo: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Manage drivers',
    breadcrumbFeatureName: JourneyFeatures.yourDriversMoreInfo.name,
    pageTitle: YOUR_DRIVER_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourDriversDetails: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Driver information',
    breadcrumbFeatureName: JourneyFeatures.yourDriversDetails.name,
    pageTitle: DRIVER_INFORMATION_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourConvictions: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Declare motoring offences',
    breadcrumbFeatureName: JourneyFeatures.yourConvictions.name,
    pageTitle: YOUR_CONVICTIONS_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourConvictionsNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourConvictionsNavigationActions.nextButtonClicked()),
    },
  ],
};

export const manageDriver: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: true,
    isShowPolicyNumber: true,
    breadcrumbFeatureName: JourneyFeatures.yourDriversPerm.name,
    pageTitle: ADD_DRIVER_PAGE_TITLE,
  },
  navConfig: [],
};

export const personalDetails: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: false,
    mainTitle: 'Add a driver',
    subTitle: 'Their personal details',
    breadcrumbFeatureName: JourneyFeatures.yourDriversPerm.name,
    pageTitle: ADD_DRIVER_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Cancel',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.nextButtonClicked()),
    },
  ],
};

export const drivingLicenseNumber: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Add a driver',
    subTitle: 'Driving licence number',
    breadcrumbFeatureName: JourneyFeatures.yourDriversPerm.name,
    pageTitle: ADD_DRIVER_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back section__button-list',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Cancel',
      id: 'cancel',
      css: 'journey-service__button-back section__button-list',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.cancelButtonClicked()),
      theme: null,
    },
    {
      label: 'Skip',
      id: 'skip',
      css: 'section__button-list',
      ariaLabel: 'Skip',
      theme: null,
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.skipButtonClicked()),
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.nextButtonClicked()),
    },
  ],
};

export const drivingHistory: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Add a driver',
    subTitle: 'Their driving history',
    breadcrumbFeatureName: JourneyFeatures.yourDriversPerm.name,
    pageTitle: ADD_DRIVER_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back section__button-list',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Cancel',
      id: 'cancel',
      css: 'journey-service__button-back section__button-list',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.cancelButtonClicked()),
      theme: null,
    },
    {
      label: 'Add this driver',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.nextButtonClicked()),
    },
  ],
};

export const chooseMainDriver: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: false,
    isShowPolicyNumber: false,
    mainTitle: 'Add a driver',
    subTitle: 'Choose main driver',
    breadcrumbFeatureName: JourneyFeatures.chooseMainDriver.name,
    pageTitle: CHOOSE_MAIN_DRIVER_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back section__button-list',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourDriversNavigationActions.nextButtonClicked()),
    },
  ],
};

// TODO: isVisible is set to false (nshathish)
export const premium: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: true,
    isShowPolicyNumber: false,
    mainTitle: '',
    breadcrumbFeatureName: JourneyFeatures.premium.name,
    pageTitle: PREMIUM_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(PremiumNavigationActions.backButtonClicked()),
      theme: null,
    },
    // TODO: As of 26th of September backend doesn't support email quote functionality by API call. TICKET: BS-177
    // {
    //   label: 'Email quote to me',
    //   id: 'emailQuote',
    //   css: 'journey-service__button-save',
    //   ariaLabel: 'Email quote to me',
    //   theme: null,
    //   onClick: () => {}
    // },
    /*{
      label: 'Save quote',
      id: 'saveQuote',
      css: 'journey-service__button-save',
      ariaLabel: 'Save quote',
      theme: null,
      onClick: (store) => store.dispatch(PremiumNavigationActions.saveQuoteButtonClicked())
    },*/
    {
      label: 'Save quote',
      id: 'cancel',
      css: null,
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(PremiumNavigationActions.cancelButtonClicked()),
      theme: 'primary',
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(PremiumNavigationActions.nextButtonClicked()),
    },
  ],
};

export const reviewFull: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: true,
    isShowPolicyNumber: false,
    mainTitle: 'Final check',
    subTitle: 'Review your policy details',
    breadcrumbFeatureName: JourneyFeatures.reviewFull.name,
    pageTitle: REVIEW_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(ReviewNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(ReviewNavigationActions.nextButtonClicked()),
    },
  ],
};

export const reviewDirectDebit: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: false,
    isShowPolicyNumber: false,
    icon: 'padlock',
    mainTitle: 'Direct Debit',
    subTitle: 'Change your Direct Debit details',
    breadcrumbFeatureName: JourneyFeatures.reviewDirectDebit.name,
    pageTitle: REVIEW_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Cancel',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(ReviewNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Submit',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(ReviewNavigationActions.nextButtonClicked()),
    },
  ],
};

export const paymentDirectDebitDisplay: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: false,
    isShowPolicyNumber: false,
    icon: 'padlock',
    mainTitle: 'Direct Debit',
    subTitle: 'Secure payment details',
    breadcrumbFeatureName: JourneyFeatures.directDebitDisplay.name,
    pageTitle: PAYMENT_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(PaymentNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(PaymentNavigationActions.nextButtonClicked()),
    },
  ],
};

export const paymentDirectDebitEdit: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: false,
    isShowPolicyNumber: false,
    icon: 'padlock',
    mainTitle: 'Direct Debit',
    subTitle: 'Update bank details',
    breadcrumbFeatureName: JourneyFeatures.directDebitEdit.name,
    pageTitle: PAYMENT_DIRECT_DEBIT_EDIT_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(PaymentNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(PaymentNavigationActions.nextButtonClicked()),
    },
  ],
};

export const prefferedPaymentDate: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: false,
    isShowPolicyNumber: false,
    icon: 'padlock',
    mainTitle: 'Direct Debit',
    subTitle: 'Change Payment Date',
    breadcrumbFeatureName: JourneyFeatures.prefferedPaymentDate.name,
    pageTitle: PREFERRED_PAYMENT_DATE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(PaymentNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(PaymentNavigationActions.nextButtonClicked()),
    },
  ],
};

export const paymentDirectDebitNew: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: false,
    isShowPolicyNumber: false,
    icon: 'padlock',
    mainTitle: 'How to pay',
    subTitle: 'Add secure payment details',
    breadcrumbFeatureName: JourneyFeatures.directDebitNew.name,
    pageTitle: PAYMENT_DIRECT_DEBIT_EDIT_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(PaymentNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(PaymentNavigationActions.nextButtonClicked()),
    },
  ],
};

export const paymentDirectDebitReview: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: false,
    isShowPolicyNumber: false,
    icon: 'padlock',
    mainTitle: 'Direct Debit',
    subTitle: 'Add secure payment details',
    breadcrumbFeatureName: JourneyFeatures.directDebitNew.name,
    pageTitle: PAYMENT_DIRECT_DEBIT_EDIT_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(PaymentNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Confirm details',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(PaymentNavigationActions.nextButtonClicked()),
    },
  ],
};

export const paymentCreditCard: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: false,
    icon: 'padlock',
    mainTitle: 'How to pay',
    subTitle: 'Secure payment details',
    breadcrumbFeatureName: JourneyFeatures.payment.name,
    pageTitle: PAYMENT_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(PaymentNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Enter card details',
      id: 'continue',
      css: 'dlg-button--no-max-width',
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(PaymentNavigationActions.nextButtonClicked()),
    },
  ],
};

export const refund: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: false,
    icon: 'padlock',
    mainTitle: `You're due a refund`,
    subTitle: 'Secure payment details',
    breadcrumbFeatureName: JourneyFeatures.refund.name,
    pageTitle: REFUND_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(RefundNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Accept refund',
      id: 'continue',
      css: null,
      ariaLabel: 'Accept refund',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(RefundNavigationActions.nextButtonClicked()),
    },
  ],
};

export const yourVehicleUsage: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Change your vehicle usage',
    breadcrumbFeatureName: JourneyFeatures.yourVehicleUsage.name,
    pageTitle: YOUR_VEHICLE_USAGE_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourVehicleUsageNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourVehicleUsageNavigationActions.nextButtonClicked()),
    },
  ],
};

export const whereIparkMyCar: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Change where your car is kept',
    breadcrumbFeatureName: JourneyFeatures.whereIparkMyCar.name,
    pageTitle: WHERE_IPARK_MY_CAR_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(WhereIparkMyCarNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(WhereIparkMyCarNavigationActions.nextButtonClicked()),
    },
  ],
};

export const parkingDetails: FeatureState = {
  panelConfig: {
    isVisible: true,
    isNotification: true,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'Change where your car is kept',
    breadcrumbFeatureName: JourneyFeatures.whereIparkMyCar.name,
    pageTitle: WHERE_IPARK_MY_CAR_TITLE,
  },
  navConfig: [
    {
      label: 'Back',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(YourAddressNavigationActions.backButtonClicked()),
      theme: null,
    },
    {
      label: 'Next',
      id: 'continue',
      css: null,
      ariaLabel: 'Continue',
      theme: 'primary',
      onClick: (store) =>
        store.dispatch(YourAddressNavigationActions.nextButtonClicked()),
    },
  ],
};

export const confirmFull: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: true,
    isShowPolicyNumber: false,
    pageTitle: CONFIRM_PAGE_TITLE,
    mainTitle: 'Confirmation',
  },
  navConfig: [],
};

const confirmDirectDebit: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: true,
    isShowPolicyNumber: false,
    pageTitle: CONFIRM_PAGE_TITLE,
  },
  navConfig: [],
};

export const confirmAutoRenewal: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: true,
    isShowPolicyNumber: false,
    pageTitle: CONFIRM_PAGE_TITLE,
  },
  navConfig: [],
};

export const errors: FeatureState = {
  panelConfig: {
    isVisible: false,
    pageTitle: ERROR_PAGE_TITLE,
  },
  navConfig: [],
};

const cancelPolicy: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: false,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'What would you like to change?',
    breadcrumbFeatureName: JourneyFeatures.cancelPolicy.name,
    pageTitle: DASHBOARD_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Cancel',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(
          MidTermCancellationNavigationActions.backButtonClicked()
        ),
      theme: null,
      isDisabled: false,
    },
  ],
};

const anotherReason: FeatureState = {
  panelConfig: {
    isVisible: false,
    isNotification: false,
    isShowPolicyNumber: true,
    mainTitle: 'Make a change',
    subTitle: 'What would you like to change?',
    breadcrumbFeatureName: JourneyFeatures.anotherReason.name,
    pageTitle: DASHBOARD_PAGE_TITLE,
  },
  navConfig: [
    {
      label: 'Cancel',
      id: 'back',
      css: 'journey-service__button-back',
      ariaLabel: 'cancel',
      onClick: (store) =>
        store.dispatch(
          MidTermCancellationNavigationActions.backButtonClicked()
        ),
      theme: null,
      isDisabled: false,
    },
  ],
};

export const JourneyConfig = {
  [JourneyFeatures.loadPolicy.path]: loadPolicy,
  [JourneyFeatures.billing.path]: billing,
  [JourneyFeatures.retrieveQuote.path]: retrieveQuote,
  [JourneyFeatures.changeType.path]: changeType,
  [JourneyFeatures.changeDate.path]: changeDate,
  [JourneyFeatures.dashboardSelect.path]: dashboardSelect,
  [JourneyFeatures.dashboardUpdate.path]: dashboardUpdate,
  [JourneyFeatures.renewalPreference.path]: renewalPreference,
  [JourneyFeatures.yourCarPerm.path]: yourCarPerm,
  [JourneyFeatures.yourCarTemp.path]: yourCarTemp,
  [JourneyFeatures.yourTemporaryEuropeanCover.path]: yourTemporaryEuropeanCover,
  [JourneyFeatures.yourMileage.path]: yourMileage,
  [JourneyFeatures.yourDetails.path]: yourDetails,
  [JourneyFeatures.yourLicenceNumber.path]: yourLicenceNumber,
  [JourneyFeatures.yourDrivingHistory.path]: yourDrivingHistory,
  [JourneyFeatures.yourAddresses.path]: yourAddresses,
  [JourneyFeatures.yourAddress.path]: yourAddress,
  [JourneyFeatures.yourPostalAddress.path]: yourPostalAddress,
  [JourneyFeatures.yourConvictions.path]: yourConvictions,
  [JourneyFeatures.yourRegistration.path]: yourRegistration,
  [JourneyFeatures.yourDriversPerm.path]: yourDriversPerm,
  [JourneyFeatures.yourDriversTemp.path]: yourDriversTemp,
  [JourneyFeatures.yourDriversMoreInfo.path]: yourDriversMoreInfo,
  [JourneyFeatures.addDriver.path]: manageDriver,
  [JourneyFeatures.editDriver.path]: manageDriver,
  [JourneyFeatures.personalDetails.path]: personalDetails,
  [JourneyFeatures.drivingLicenseNumber.path]: drivingLicenseNumber,
  [JourneyFeatures.drivingHistory.path]: drivingHistory,
  [JourneyFeatures.chooseMainDriver.path]: chooseMainDriver,
  [JourneyFeatures.yourDriversDetails.path]: yourDriversDetails,
  [JourneyFeatures.allocate.path]: allocate,
  [JourneyFeatures.premium.path]: premium,
  [JourneyFeatures.reviewFull.path]: reviewFull,
  [JourneyFeatures.reviewDirectDebit.path]: reviewDirectDebit,
  [JourneyFeatures.creditCard.path]: paymentCreditCard,
  [JourneyFeatures.directDebitDisplay.path]: paymentDirectDebitDisplay,
  [JourneyFeatures.directDebitEdit.path]: paymentDirectDebitEdit,
  [JourneyFeatures.prefferedPaymentDate.path]: prefferedPaymentDate,
  [JourneyFeatures.directDebitNew.path]: paymentDirectDebitNew,
  [JourneyFeatures.directDebitReview.path]: paymentDirectDebitReview,
  [JourneyFeatures.refund.path]: refund,
  [JourneyFeatures.confirmFull.path]: confirmFull,
  [JourneyFeatures.confirmDirectDebit.path]: confirmDirectDebit,
  [JourneyFeatures.confirmAutoRenewal.path]: confirmAutoRenewal,
  [JourneyFeatures.yourVehicleUsage.path]: yourVehicleUsage,
  [JourneyFeatures.whereIparkMyCar.path]: whereIparkMyCar,
  [JourneyFeatures.parkingDetails.path]: parkingDetails,
  [JourneyFeatures.errors.path]: errors,
  [JourneyFeatures.anotherReason.path]: anotherReason,
  [JourneyFeatures.cancelPolicy.path]: cancelPolicy,
};
